(() => {
  const ACCESS_KEY = 'prn-newsletter-gated-access-v1';
  const GRANTED_VALUE = 'granted';
  const PIXELATED_TEXT_SELECTOR = '[data-newsletter-gated-pixelated-value]';
  const PIXELATED_TEXT_CANVAS_CLASS = 'newsletter-gated-product__pixelated-text-canvas';
  const PIXELATED_TEXT_VALUE_ID = 'data-newsletter-gated-pixelated-value-id';
  const PIXELATED_TEXT_PENDING = 'data-newsletter-gated-pixelated-pending';
  const GATED_CARD_LINK_SELECTOR = '[data-newsletter-gated-card-link]';
  const GATED_CARD_HREF_ATTRIBUTE = 'data-newsletter-gated-card-href';
  const GATED_CARD_OVERLAY_SELECTOR = '[data-newsletter-gated-card-overlay]';
  const PIXELATED_IMAGE_SELECTOR = [
    '.newsletter-gated-product--card .card__media .media--hover-effect > img',
    '.newsletter-gated-product .product__media-wrapper .product__media > img:not(.preloader-image)',
    '.newsletter-gated-product .product__media-wrapper .deferred-media__poster > img',
    '.newsletter-gated-product .product__media-wrapper .thumbnail > img',
    '.newsletter-gated-product .product__lead-image .product__image > modal-opener > button > img',
    '.newsletter-gated-product .product__description img',
    '.newsletter-gated-product .product-popup-modal__content-info > img',
    '.newsletter-gated-product .product-media-modal__content > img',
    '.newsletter-gated-product .product-media-modal__content .deferred-media__poster > img',
  ].join(',');
  const pixelatedImages = new Map();
  const processingImages = new WeakSet();
  const waitingImages = new WeakSet();
  const pixelatedTextValues = new Map();
  const processingTextValues = new Map();
  let textValueSequence = 0;
  const integrationConfigElement = document.getElementById('StorefrontIntegrationConfig');
  let integrationConfig = {};

  try {
    integrationConfig = integrationConfigElement ? JSON.parse(integrationConfigElement.textContent) : {};
  } catch (error) {
    integrationConfig = {};
  }

  const klaviyoFormId = String(integrationConfig.klaviyoFormId || '').trim();

  const storedAccessIsGranted = () => {
    try {
      return window.localStorage.getItem(ACCESS_KEY) === GRANTED_VALUE;
    } catch (error) {
      return false;
    }
  };

  const accessIsGranted = () =>
    document.documentElement.classList.contains('newsletter-gated-unlocked') || storedAccessIsGranted();

  const pixelationIsAvailable = () => typeof window.pixelit === 'function';

  const textPixelationIsAvailable = () =>
    pixelationIsAvailable() && typeof window.html2canvas === 'function';

  const getOriginalImageAttributes = (image) => {
    const attributes = {};
    ['src', 'srcset', 'sizes'].forEach((name) => {
      attributes[name] = image.hasAttribute(name) ? image.getAttribute(name) : null;
    });
    return attributes;
  };

  const restorePixelatedImage = (image, state) => {
    const originalAttributes = state.originalAttributes;
    Object.keys(originalAttributes).forEach((name) => {
      const value = originalAttributes[name];
      if (value === null) image.removeAttribute(name);
      else image.setAttribute(name, value);
    });
    window.URL.revokeObjectURL(state.objectUrl);
    pixelatedImages.delete(image);
  };

  const removePixelation = () => {
    pixelatedImages.forEach((state, image) => restorePixelatedImage(image, state));
  };

  const replaceWithPixelatedImage = (image, source) => {
    if (accessIsGranted() || pixelatedImages.has(image) || !image.parentElement) {
      processingImages.delete(image);
      return;
    }

    const canvas = document.createElement('canvas');

    try {
      new window.pixelit({ from: source, to: canvas, scale: 4 }).draw().pixelate();
    } catch (error) {
      processingImages.delete(image);
      return;
    }

    canvas.toBlob((blob) => {
      processingImages.delete(image);
      if (!blob || accessIsGranted() || !image.parentElement) return;

      const objectUrl = window.URL.createObjectURL(blob);
      const originalAttributes = getOriginalImageAttributes(image);

      if (accessIsGranted()) {
        window.URL.revokeObjectURL(objectUrl);
        return;
      }

      pixelatedImages.set(image, { objectUrl, originalAttributes });
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.setAttribute('src', objectUrl);
    }, 'image/png');
  };

  const pixelateImage = (image) => {
    if (accessIsGranted() || pixelatedImages.has(image) || processingImages.has(image) || !pixelationIsAvailable()) return;

    if (!image.complete || !image.naturalWidth) {
      if (!waitingImages.has(image)) {
        waitingImages.add(image);
        image.addEventListener(
          'load',
          () => {
            waitingImages.delete(image);
            pixelateImage(image);
          },
          { once: true }
        );
      }
      return;
    }

    const sourceUrl = image.currentSrc || image.src;
    if (!sourceUrl) return;

    processingImages.add(image);
    const source = document.createElement('img');
    source.crossOrigin = 'anonymous';
    source.decoding = 'async';
    let sourceSettled = false;

    const renderFromSource = (drawFrom) => {
      if (sourceSettled) return;
      sourceSettled = true;
      replaceWithPixelatedImage(image, drawFrom);
    };

    source.addEventListener('load', () => renderFromSource(source), { once: true });
    source.addEventListener(
      'error',
      () => {
        if (!sourceSettled) {
          sourceSettled = true;
          processingImages.delete(image);
        }
      },
      { once: true }
    );
    source.src = sourceUrl;

    if (source.complete && source.naturalWidth) renderFromSource(source);
  };

  const getOriginalTextAttributes = (element) => ({
    valueId: element.getAttribute(PIXELATED_TEXT_VALUE_ID),
    inert: element.hasAttribute('inert'),
    ariaHidden: element.getAttribute('aria-hidden'),
  });

  const restoreTextAttributes = (element, state) => {
    if (state.originalAttributes.valueId === null) element.removeAttribute(PIXELATED_TEXT_VALUE_ID);
    else element.setAttribute(PIXELATED_TEXT_VALUE_ID, state.originalAttributes.valueId);

    if (state.originalAttributes.inert) element.setAttribute('inert', '');
    else element.removeAttribute('inert');

    if (state.originalAttributes.ariaHidden === null) element.removeAttribute('aria-hidden');
    else element.setAttribute('aria-hidden', state.originalAttributes.ariaHidden);

    element.removeAttribute(PIXELATED_TEXT_PENDING);
  };

  const removePixelatedTextValues = () => {
    processingTextValues.forEach((state, element) => restoreTextAttributes(element, state));
    processingTextValues.clear();
    pixelatedTextValues.forEach((state, element) => {
      state.canvas.remove();
      restoreTextAttributes(element, state);
    });
    pixelatedTextValues.clear();
  };

  const cloneTextValueForCapture = (element, valueId) =>
    window.html2canvas(element, {
      backgroundColor: null,
      logging: false,
      scale: 1,
      useCORS: true,
      onclone: (clonedDocument) => {
        const clone = clonedDocument.querySelector(`[${PIXELATED_TEXT_VALUE_ID}="${valueId}"]`);
        let node = clone;
        while (node && node !== clonedDocument.documentElement) {
          node.style.opacity = '1';
          node.style.visibility = 'visible';
          node.removeAttribute('aria-hidden');
          node.removeAttribute('inert');
          node = node.parentElement;
        }
      },
    });

  const pixelateTextValue = (element) => {
    if (!textPixelationIsAvailable() || accessIsGranted() || pixelatedTextValues.has(element) || processingTextValues.has(element)) return;

    const originalAttributes = getOriginalTextAttributes(element);
    const valueId = originalAttributes.valueId || `value-${++textValueSequence}`;
    const state = { originalAttributes, valueId };
    processingTextValues.set(element, state);
    element.setAttribute(PIXELATED_TEXT_VALUE_ID, valueId);
    element.setAttribute('inert', '');
    element.setAttribute('aria-hidden', 'true');

    cloneTextValueForCapture(element, valueId)
      .then((sourceCanvas) => {
        if (processingTextValues.get(element) !== state || accessIsGranted()) {
          if (processingTextValues.get(element) === state) {
            processingTextValues.delete(element);
            restoreTextAttributes(element, state);
          }
          return;
        }

        const pixelatedCanvas = document.createElement('canvas');
        try {
          new window.pixelit({ from: sourceCanvas, to: pixelatedCanvas, scale: 4 }).draw().pixelate();
        } catch (error) {
          processingTextValues.delete(element);
          restoreTextAttributes(element, state);
          return;
        }

        pixelatedCanvas.className = PIXELATED_TEXT_CANVAS_CLASS;
        pixelatedCanvas.setAttribute('aria-hidden', 'true');
        element.appendChild(pixelatedCanvas);
        pixelatedTextValues.set(element, { ...state, canvas: pixelatedCanvas });
        processingTextValues.delete(element);
      })
      .catch(() => {
        if (processingTextValues.get(element) === state) {
          processingTextValues.delete(element);
          restoreTextAttributes(element, state);
        }
      });
  };

  const syncPixelatedText = (unlocked) => {
    if (unlocked) {
      removePixelatedTextValues();
      return;
    }

    if (!textPixelationIsAvailable()) {
      document.documentElement.classList.remove('newsletter-gated-pixelation-enabled');
      return;
    }
    document.documentElement.classList.add('newsletter-gated-pixelation-enabled');
    document.querySelectorAll(PIXELATED_TEXT_SELECTOR).forEach((element) => {
      element.setAttribute(PIXELATED_TEXT_PENDING, '');
      pixelateTextValue(element);
    });
  };

  const syncGatedCardNavigation = (unlocked) => {
    document.querySelectorAll(GATED_CARD_LINK_SELECTOR).forEach((link) => {
      const href = link.getAttribute(GATED_CARD_HREF_ATTRIBUTE);
      if (!href) return;

      if (unlocked) {
        link.setAttribute('href', href);
        link.removeAttribute('aria-disabled');
        link.removeAttribute('tabindex');
      } else {
        link.removeAttribute('href');
        link.setAttribute('aria-disabled', 'true');
        link.setAttribute('tabindex', '-1');
      }
    });

    document.querySelectorAll(GATED_CARD_OVERLAY_SELECTOR).forEach((overlay) => {
      overlay.hidden = unlocked;
      overlay.setAttribute('aria-hidden', String(unlocked));
    });
  };

  const syncPixelatedMedia = (unlocked) => {
    if (unlocked) {
      removePixelation();
      return;
    }

    if (!pixelationIsAvailable()) return;
    document.querySelectorAll(PIXELATED_IMAGE_SELECTOR).forEach(pixelateImage);
  };

  const applyGatedState = () => {
    const unlocked = accessIsGranted();
    document.documentElement.classList.toggle('newsletter-gated-unlocked', unlocked);
    syncGatedCardNavigation(unlocked);

    document.querySelectorAll('[data-newsletter-gated-product="true"]').forEach((product) => {
      product.classList.toggle('newsletter-gated-product--unlocked', unlocked);

      product.querySelectorAll('[data-newsletter-gated-locked-action]').forEach((action) => {
        action.hidden = unlocked;
        action.setAttribute('aria-hidden', String(unlocked));
      });

      product.querySelectorAll('[data-newsletter-gated-purchase]').forEach((purchase) => {
        purchase.hidden = !unlocked;
        purchase.setAttribute('aria-hidden', String(!unlocked));
      });
    });

    syncPixelatedMedia(unlocked);
    syncPixelatedText(unlocked);
  };

  const grantAccess = () => {
    try {
      window.localStorage.setItem(ACCESS_KEY, GRANTED_VALUE);
    } catch (error) {
      // The current page can still unlock if storage is unavailable.
    }

    document.documentElement.classList.add('newsletter-gated-unlocked');
    applyGatedState();
    document.dispatchEvent(new CustomEvent('newsletterGatedProductsUnlocked'));
  };

  const signupSucceededOnThisPage = () =>
    Boolean(document.querySelector('[data-newsletter-signup-success]'));

  applyGatedState();

  if (signupSucceededOnThisPage()) {
    grantAccess();
  }

  // Klaviyo emits this event for onsite form activity. Scope it to the
  // configured newsletter form so another Klaviyo form cannot unlock products.
  window.addEventListener('klaviyoForms', (event) => {
    const detail = event.detail || {};
    const formId = String(detail.formId || '').trim();
    if (detail.type === 'submit' && klaviyoFormId && formId === klaviyoFormId) {
      grantAccess();
    }
  });

  // Native signup surfaces can dispatch this without knowing anything about the
  // gated-product implementation.
  document.addEventListener('newsletterSignupSuccess', grantAccess);

  document.addEventListener(
    'click',
    (event) => {
      const link = event.target.closest?.(GATED_CARD_LINK_SELECTOR);
      if (link && !accessIsGranted()) {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    true
  );

  window.addEventListener('storage', (event) => {
    if (event.key === ACCESS_KEY) applyGatedState();
  });

  const refreshGatedState = () => {
    if (signupSucceededOnThisPage()) {
      grantAccess();
    } else {
      applyGatedState();
    }
  };

  document.addEventListener('shopify:section:load', refreshGatedState);

  // Collection/product section updates can insert new gated media without a full page load.
  if (typeof MutationObserver !== 'undefined' && document.body) {
    const observer = new MutationObserver((mutations) => {
      if (mutations.some((mutation) => mutation.addedNodes.length > 0)) {
        refreshGatedState();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }
})();
